import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { AnimatePresence, motion } from "motion/react";
import { createContext, type ReactNode, useCallback, useContext, useRef, useState } from "react";
import { Avatar } from "../components/ui";
import { api } from "./api";
import { formatPhone } from "./format";
import { notificationsQuery } from "./queries";
import type { NotificationItem, NotificationKind } from "./types";

/** A notification the API just stored for this person (LiveEvent "notification"). */
export interface NotificationEvent {
  id: string;
  kind: NotificationKind;
  conversationId: string;
}

/** What this person wants to be told with, per device (like the theme). */
export interface NotifyPrefs {
  /** A card on the screen. */
  cards: boolean;
  sound: boolean;
  /** The system notification, when Orbis is in another tab or minimized. */
  browser: boolean;
}

const KEY = "orbis.notify";
const DEFAULTS: NotifyPrefs = { cards: true, sound: true, browser: false };

export function notifyPrefs(): NotifyPrefs {
  try {
    return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(KEY) ?? "{}") };
  } catch {
    return DEFAULTS;
  }
}

export function setNotifyPrefs(prefs: NotifyPrefs) {
  try {
    localStorage.setItem(KEY, JSON.stringify(prefs));
  } catch {
    // Private mode: the choice lasts this visit only.
  }
}

let audio: AudioContext | null = null;

/** Two short soft notes, made on the spot (no sound file to download). */
export function chime() {
  try {
    audio ??= new AudioContext();
    if (audio.state === "suspended") void audio.resume();
    const now = audio.currentTime;
    for (const [i, freq] of [880, 1320].entries()) {
      const osc = audio.createOscillator();
      const gain = audio.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const start = now + i * 0.12;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.18, start + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.28);
      osc.connect(gain).connect(audio.destination);
      osc.start(start);
      osc.stop(start + 0.3);
    }
  } catch {
    // No audio on this device.
  }
}

/** The notification in the panel's words. */
export function notificationTitle(n: Pick<NotificationItem, "kind" | "count">) {
  if (n.kind === "assigned") return "Te asignaron una conversación";
  if (n.kind === "unassigned") return "Conversación sin responsable";
  return n.count > 1 ? `${n.count} mensajes nuevos` : "Mensaje nuevo";
}

export function notificationText(n: Pick<NotificationItem, "kind" | "preview">) {
  if (n.kind === "unassigned")
    return n.preview ? `${n.preview} · Nadie la tiene: asígnala.` : "Nadie la tiene: asígnala en la Bandeja.";
  return n.preview ?? "";
}

const AttentionContext = createContext<(e: NotificationEvent) => void>(() => {});

/** Tells the person about a notification the API stored for them. */
export const useAttention = () => useContext(AttentionContext);

const CARD_MS = 7_000;
const MAX_CARDS = 3;

/** Is this conversation open in front of the person right now? */
const inFront = (conversationId: string) =>
  document.visibilityState === "visible" &&
  window.location.pathname.startsWith("/bandeja") &&
  new URLSearchParams(window.location.search).get("c") === conversationId;

/**
 * The API decides who each notification is for and sends it only to them; here it is
 * shown as a card, a tone and, with Orbis in another tab, the browser's notification.
 * A new message in the chat already open is marked read instead.
 */
export function NotificationsProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [cards, setCards] = useState<(NotificationItem & { key: string })[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((key: string) => {
    clearTimeout(timers.current.get(key));
    timers.current.delete(key);
    setCards((list) => list.filter((c) => c.key !== key));
  }, []);

  const open = useCallback(
    (conversationId: string) => navigate({ to: "/bandeja", search: { c: conversationId, f: "all" } }),
    [navigate],
  );

  const attention = useCallback(
    async (e: NotificationEvent) => {
      if (e.kind === "message" && inFront(e.conversationId)) {
        await api.post("/notifications/read", { conversationId: e.conversationId }).catch(() => {});
        qc.invalidateQueries({ queryKey: ["notifications"] });
        return;
      }
      const list = await qc.fetchQuery({ ...notificationsQuery, staleTime: 0 }).catch(() => null);
      const n = list?.items.find((i) => i.id === e.id);
      if (!n) return;
      const prefs = notifyPrefs();
      if (prefs.sound) chime();
      const who = n.contactName ?? formatPhone(n.contactWaId);
      if (
        prefs.browser &&
        document.visibilityState !== "visible" &&
        "Notification" in window &&
        Notification.permission === "granted"
      ) {
        const sys = new Notification(`${notificationTitle(n)} · ${who}`, {
          body: notificationText(n),
          tag: n.id,
          icon: "/brand/orbis-simbolo-negativo.svg",
        });
        sys.onclick = () => {
          window.focus();
          open(n.conversationId);
          sys.close();
        };
      }
      if (!prefs.cards) return;
      const key = `${n.id}:${n.updatedAt}`;
      setCards((shown) => [
        { ...n, key },
        ...shown.filter((c) => c.conversationId !== n.conversationId).slice(0, MAX_CARDS - 1),
      ]);
      timers.current.set(
        key,
        setTimeout(() => dismiss(key), CARD_MS),
      );
    },
    [qc, open, dismiss],
  );

  return (
    <AttentionContext.Provider value={attention}>
      {children}
      <div className="notify-stack" aria-live="polite">
        <AnimatePresence initial={false}>
          {cards.map((c) => (
            <motion.div
              key={c.key}
              className="notify-card"
              layout
              initial={{ opacity: 0, x: 40, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: 40, transition: { duration: 0.15 } }}
              transition={{ type: "spring", stiffness: 420, damping: 32 }}
              role="status"
            >
              <button
                className="notify-open"
                type="button"
                onClick={() => {
                  open(c.conversationId);
                  dismiss(c.key);
                }}
              >
                <Avatar name={c.contactName} fallback={c.contactWaId} />
                <span className="notify-text">
                  <span className="notify-title">{notificationTitle(c)}</span>
                  <span className="notify-who">{c.contactName ?? formatPhone(c.contactWaId)}</span>
                  {notificationText(c) && <span className="notify-body">{notificationText(c)}</span>}
                </span>
              </button>
              <button className="notify-close" type="button" aria-label="Cerrar aviso" onClick={() => dismiss(c.key)}>
                ×
              </button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </AttentionContext.Provider>
  );
}
